import { useEffect } from 'react';
import { init, replayIntegration, reactRouterV6BrowserTracingIntegration } from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { RequestError } from '@myhealth-raccoon/services';

function isRequestError(test: unknown): test is RequestError {
    return !!test && typeof test === 'object' && 'name' in test && test.name === 'Request Error';
}

if (import.meta.env.PROD) {
    init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [
            // See docs for support of different versions of variation of react router
            // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
            reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            replayIntegration(),
        ],
        beforeSend: (event, hint) => {
            const exception = hint.originalException;
            if (exception && isRequestError(exception)) {
                event.tags = event.tags || {};
                event.tags['http.url'] = exception.url;
                event.tags['http.status_code'] = exception.statusCode;
                event.tags['http.method'] = exception.method;
                if (exception.requestBody) {
                    event.tags['http.data'] = JSON.stringify(exception.requestBody);
                }
            }
            return event;
        },
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for tracing.
        // Learn more at
        // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
        tracesSampleRate: 1.0,
        environment: import.meta.env.PROD ? import.meta.env.VITE_SENTRY_ENVIRONMENT : 'local',
        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        tracePropagationTargets: [
            /^\//,
            import.meta.env.VITE_AUTH_API_URL,
            import.meta.env.VITE_BP_API_URL,
            import.meta.env.VITE_MYHEALTH_WEBSITE,
            import.meta.env.VITE_AUTOMED_URL,
        ],

        // Capture Replay for 10% of all sessions,
        // plus for 100% of sessions with an error
        // Learn more at
        // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
}
